<template>
	<div id="app" class="app-container">
		<loader-bar />
		<alert-container />
		<router-view class="app-layout" />
	</div>
</template>
<script>
import LoaderBar from './components/LoaderBar'
import AlertContainer from '@/components/Alert/Container.vue'
import InteractsWithBodyClass from './mixins/InteractsWithBodyClass'
import {
	COPIED_TEXT_EXPIRY_DURATION,
	INSIGHT_CONFIG_LOCAL_STORAGE,
} from '@/config/constants'
import { SET_APP_USER_INTERACTION_RECEIVED } from '@/store/mutation-types'
import { mapGetters } from 'vuex'
import util from '@/util'
import intercom from '@/services/intercom'

export default {
	/**
	 * The component's registered child components.
	 *
	 * @type {Object}
	 */
	components: {
		LoaderBar,
		AlertContainer,
	},

	/**
	 * The component's computed properties.
	 *
	 * @type {Object}
	 */
	computed: {
		...mapGetters('partners', [
			'isEngageApp',
			'clarusAppType',
			'engageOnlyAppWithoutAccess',
		]),
		...mapGetters('auth', [
			'isOrganizationOnlyAdmin',
			'organizationId',
			'isAuthorizedOrganizationId',
		]),
	},

	/**
	 * The component's local methods.
	 *
	 * @type {Object}
	 */
	methods: {
		/**
		 * Sync organization-only admins to /organization/:id.
		 * Everyone else on that route goes to Calls.
		 *
		 * @param {Object} route
		 * @return {void}
		 */
		redirectOrganizationOnlyAdmin(route = this.$route) {
			if (!route.matched.some(matchedRoute => matchedRoute.meta.auth)) {
				return
			}

			if (this.isOrganizationOnlyAdmin && this.organizationId) {
				if (
					route.name === 'app.organization' &&
					this.isAuthorizedOrganizationId(route.params.organizationId)
				) {
					return
				}

				this.$router
					.push({
						name: 'app.organization',
						params: {
							organizationId: this.organizationId,
						},
					})
					.catch(() => {})
				return
			}

			if (route.name !== 'app.organization') {
				return
			}

			if (this.isAuthorizedOrganizationId(route.params.organizationId)) {
				return
			}

			const partnerId = this.$store.getters.partner?.id
			if (!partnerId) {
				return
			}

			this.$router
				.push({
					name: 'app.calls',
					params: {
						partner: partnerId,
					},
				})
				.catch(() => {})
		},

		/**
		 * Detect if the user is using IE or Edge.
		 *
		 * @return {Boolean}
		 */
		isInternetExplorerOrEdge() {
			const userAgent = navigator.userAgent

			if (userAgent.indexOf('MSIE ') > -1) {
				return true
			}

			if (userAgent.indexOf('Trident/') > -1) {
				return true
			}

			if (userAgent.indexOf('Edge/') > -1) {
				return true
			}

			return false
		},

		// Handle window focus event to clear clipboard if more than 60 seconds have passed
		handleWindowFocus() {
			const copiedTimestamp = localStorage.getItem(
				'copiedSummaryTimestamp'
			)
			if (copiedTimestamp) {
				const currentTime = new Date().getTime()
				const elapsedTime = currentTime - parseInt(copiedTimestamp, 10)
				if (elapsedTime > COPIED_TEXT_EXPIRY_DURATION) {
					util.copyToClipboard(' ')
					localStorage.removeItem('copiedSummaryTimestamp')
				}
			}
		},

		onAppFirstPointerCapture() {
			this.$store.commit(
				`settings/${SET_APP_USER_INTERACTION_RECEIVED}`,
				true
			)
		},
	},

	/**
	 * The component's mergeable mixins.
	 *
	 * @type {Array}
	 */
	mixins: [InteractsWithBodyClass],

	/**
	 * The component's name used for debugging.
	 *
	 * @type {String}
	 */
	name: 'App',

	/**
	 * The component's property watchers.
	 *
	 * @type {Object}
	 */
	watch: {
		/**
		 * Watch the route for changes.
		 *
		 * @return {void}
		 */
		$route(to, from) {
			if (
				to.name !== 'app.dashboard' &&
				to.name !== from.name &&
				localStorage.getItem(INSIGHT_CONFIG_LOCAL_STORAGE)
			) {
				localStorage.removeItem(INSIGHT_CONFIG_LOCAL_STORAGE)
			}
			intercom.updateRoute()
			if (this.isIE) {
				this.$alert.error('browser.error')
			}
			if (
				to.params.partner !== from.params.partner &&
				!this.clarusAppType &&
				this.isEngageApp &&
				to.name !== 'app.engage'
			) {
				this.$router.push({ name: 'app.engage' })
			}
			this.redirectOrganizationOnlyAdmin(to)
			if (
				to.params.partner !== from.params.partner &&
				this.engageOnlyAppWithoutAccess &&
				to.name !== 'app.fallback'
			) {
				this.$router.push({ name: 'app.fallback' })
			}
		},

		isOrganizationOnlyAdmin: {
			immediate: true,
			handler() {
				this.redirectOrganizationOnlyAdmin(this.$route)
			},
		},
		/**
		 * Partner apps can resolve after refresh; re-check redirect then.
		 */
		'$store.getters.partner.id'() {
			this.redirectOrganizationOnlyAdmin(this.$route)
		},
	},

	/**
	 * The component's mounted lifecycle hook.
	 *
	 * @return {void}
	 */
	mounted() {
		window.addEventListener('focus', this.handleWindowFocus)
		document.addEventListener(
			'pointerdown',
			this.onAppFirstPointerCapture,
			{ capture: true, once: true }
		)
	},

	beforeDestroy() {
		window.removeEventListener('focus', this.handleWindowFocus)
		document.removeEventListener(
			'pointerdown',
			this.onAppFirstPointerCapture,
			{ capture: true }
		)
	},

	/**
	 * The component's created lifecycle hook.
	 *
	 * @return {void}
	 */
	created() {
		if (this.isInternetExplorerOrEdge()) {
			this.isIE = true
		}
	},

	/**
	 * Get the component's initial state.
	 *
	 * @return {Object}
	 */
	data() {
		return {
			isIE: false,
		}
	},
}
</script>
<style lang="scss">
@import './sass/app.scss';
</style>
